const ASSISTANT_LOADER_PATH = '/assets/scripts/levCustomerAssistantLoader.js';
const DEFAULT_ASSISTANT_EMBED_POSITION = 'bottom-right';

export type AssistantEmbedPosition = 'bottom-right' | 'bottom-left';

function normalizeBasePath(pathname: string) {
	if (pathname === '/' || pathname.length === 0) {
		return '';
	}
	return pathname.replace(/\/$/, '');
}
function getNormalizedAssistantUrl() {
	const configuredAssistantUrl = import.meta.env.VITE_ASSISTANT_BASE_URL;
	return configuredAssistantUrl.replace(/\/$/, '');
}

export function getAssistantBasename() {
	const pathname = new URL(getNormalizedAssistantUrl()).pathname;
	return normalizeBasePath(pathname);
}

/**
 * Returns the environment specific starter URL for the assistant
 *
 * Example:
 * localhost:3000 -> http://localhost:3000/assistant.html/<agentTemplateId>
 * preview -> https://123.levitate-internal.pages.dev/assistant/<agentTemplateId>
 * test -> https://assistant-test.levitate.ai/<agentTemplateId>
 * production -> https://assistant.levitate.ai/<agentTemplateId>
 */
export function getAssistantStarterUrl({ agentTemplateId }: { agentTemplateId: string }) {
	return `${getNormalizedAssistantUrl()}/${agentTemplateId}`;
}

function normalizeEmbedPosition(position?: AssistantEmbedPosition): AssistantEmbedPosition {
	if (position === 'bottom-left') {
		return 'bottom-left';
	}

	return DEFAULT_ASSISTANT_EMBED_POSITION;
}

function normalizeLauncherLabel(launcherLabel?: string) {
	if (launcherLabel == null) {
		return '';
	}

	return launcherLabel.trim();
}

function escapeHtmlAttribute(value: string) {
	return value
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

export function getAssistantEmbedSnippet({
	agentTemplateId,
	position,
	launcherLabel,
	primaryColor,
	foregroundColor,
}: {
	agentTemplateId: string;
	position?: AssistantEmbedPosition;
	launcherLabel?: string;
	primaryColor?: string;
	foregroundColor?: string;
}) {
	const assistantUrl = getNormalizedAssistantUrl();
	const assistantOrigin = new URL(assistantUrl).origin;
	const loaderScriptUrl = `${assistantOrigin}${ASSISTANT_LOADER_PATH}`;
	const normalizedPosition = normalizeEmbedPosition(position);
	const normalizedLauncherLabel = normalizeLauncherLabel(launcherLabel);

	const colorAttrs = [
		primaryColor ? ` data-primary-color="${escapeHtmlAttribute(primaryColor)}"` : '',
		foregroundColor ? ` data-foreground-color="${escapeHtmlAttribute(foregroundColor)}"` : '',
	].join('');

	return `<script async src="${escapeHtmlAttribute(loaderScriptUrl)}" data-agent-template-id="${escapeHtmlAttribute(
		agentTemplateId
	)}" data-app-url="${escapeHtmlAttribute(assistantUrl)}" data-position="${escapeHtmlAttribute(
		normalizedPosition
	)}" data-launcher-label="${escapeHtmlAttribute(normalizedLauncherLabel)}"${colorAttrs}></script>`;
}
